import { useForm } from "react-hook-form";
import Input from "@/components/atoms/Input";
import { Switch } from "@/components/atoms/Switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { router } from "@inertiajs/react";
import { ColumnDef } from "@tanstack/react-table";
import type Entity from "@/types/Account/AccountEntitiy";
import TabTable, { SetData, SetOpen } from "./TabTable";
import type FormFieldItem from "@/types/Form/FormFieldItem";
import Button from "@/components/atoms/Button";
import { Plus, SquarePen } from "lucide-react";
import MultipleSelect from "@/components/atoms/MultipleSelect";
import { useEffect, useState } from "react";
import TabSheet from "./TabSheet";
import useAccess from "@/hooks/useAccess";
import Badge from "@/components/atoms/Badge";
import * as Constants from "@/constants";
import { NMQEntity } from "@/types/Account/AccountEntitiy";
import stringSchema from "@/lib/utils/stringSchema";
import Pagination from "@/components/atoms/Pagination";
import { capitalize } from "@/lib/utils";

const formSchema = z.object({
  name: stringSchema({ key: "Name", required: true }),
  exact_id: z.string().optional(),
  status: z.boolean(),
});

type Props = {
  accountId: number;
  data: Entity[];
  nmqEntities: NMQEntity[];
  redirect_props?: any;
};

const EntityTab = ({ data, accountId, nmqEntities, redirect_props }: Props) => {
  const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
  const totalCount = data.length;
  const totalPages = Math.ceil(totalCount / pagination.per_page);
  const [loading, setLoading] = useState(false);
  const { getAccess } = useAccess();
  const [edit, setEdit] = useState<boolean>(false);
  const [createOpen, setCreateOpen] = useState<boolean>(false);
  
  const defaultValues = {
    name: "",
    exact_id: "",
    nmq_entity_ids: [] as string[],
    status: true,
    show_disclaimer_message: true,
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const getColumns = (
    setOpen: SetOpen,
    setData: SetData
  ): ColumnDef<Entity>[] => [
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "exact_id",
      header: "Exact ID",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div className="space-x-2">
          <Badge
            variant={row.original.status ? "success" : "destructive"}
            className="p-1"
          />
          <span>
            {row.original.status
              ? Constants.STATUSES.ACTIVE
              : Constants.STATUSES.INACTIVE}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (row: any) => (
        <SquarePen
          className="cursor-pointer"
          size={20}
          style={{ opacity: !getAccess(["update_entity"]) ? 0.5 : 1, pointerEvents: !getAccess(["update_entity"]) ? 'none' : 'auto' }}
          onClick={() => {
            setData(row.row.original);
            setOpen(true);
          }}
        />
      ),
      size: 20,
    },
  ];

  const onSubmit = (
    mode: "edit" | "create",
    data: any,
    sheetData: Entity | null
  ) => {
    const values = form.getValues();

    const payload = {
      entity: {
        name: values.name || "",
        exact_id: parseInt(values.exact_id),
        nmq_entity_ids: values.nmq_entity_ids || [],
        status: values.status,
        account_id: accountId,
        show_disclaimer_message: values.show_disclaimer_message,
      },
    };

    if (mode === "edit") {
      router.put(`/entities/${sheetData?.id}`, payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    } else if (mode === "create") {
      router.post(`/entities`, payload, {
        onStart: () => setLoading(true),
        onFinish: () => setLoading(false),
      });
    }
  };

  const formFields: FormFieldItem<Entity>[] = [
    {
      label: "Name",
      key: "name",
      required: true,
      render: (props) => (
        <Input
          placeholder="Name"
          danger={
            !!form.formState.errors[
              props.name as keyof typeof form.formState.errors
            ]
          }
          disabled={!edit && !createOpen}
          className=""
          {...props}
        />
      ),
    },
    {
      key: "exact_id",
      label: "Exact ID",
      render: (props) => (
        <Input
          placeholder="Exact ID"
          danger={
            !!form.formState.errors[
              props.name as keyof typeof form.formState.errors
            ]
          }
          disabled={!edit && !createOpen}
          className=""
          {...props}
          value={props.value || ""}
          onChange={(e) => {
            props.onChange(e.target.value);
          }}
          type="number"
        />
      ),
    },
    {
      key: "nmq_entity_ids",
      label: "NMQ Entities",
      render: (props) => (
        <MultipleSelect
          options={
            nmqEntities
              .filter((ent) => ent.status)
              .map((ent) => ({
                value: ent.id,
                label: ent.name,
              })) || []
          }
          value={props.value || []}
          onChange={props.onChange}
          disabled={!edit && !createOpen}
          placeholder="Select NMQ Entities"
          className=""
        />
      ),
    },
    {
      key: "show_disclaimer_message",
      label: "Show Disclaimer Message",
      render: (props) => (
        <div>
          <Switch
            checked={props.value}
            disabled={!edit && !createOpen && getAccess(["set_show_disclaimer_message"])}
            onCheckedChange={props.onChange}
          />
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (props) => (
        <div>
          <Switch
            checked={props.value}
            disabled={!edit && !createOpen}
            onCheckedChange={props.onChange}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (createOpen) form.reset(defaultValues);
  }, [createOpen]);

  const handlePageChange = (page: number) => { 
    setPagination(prev => ({ ...prev, page }));
  };

  useEffect(() => {
    if (redirect_props && redirect_props.errors) {
      const errors = redirect_props.errors;
      Object.entries(errors).forEach(([key, messages]) => {
        if (Array.isArray(messages)) {
          form.setError(key as keyof typeof form.getValues, {
            type: "manual",
            message: `${capitalize(key)} ${messages[0]}`,
          });
        }
      });
    } else {
      setCreateOpen(false);
      setEdit(false);
    }
  }, [redirect_props]);

  return (
    <div>
     
      <Button
        className="float-right m-4"
        disabled={!getAccess(["create_entity"])}
        onClick={() => {
          setCreateOpen(true);
        }}
      >
        <Plus size={16} className="mr-2" />
        Add New Entity
      </Button>
 
      <TabTable
        columns={getColumns}
        data={data.map((d) => ({
          ...d,
          nmq_entity_ids: d.nmq_entities.map((ent) => ent.id),
        }))}
        form={form}
        formFields={formFields}
        onSubmit={onSubmit}
        edit={edit}
        setEdit={setEdit}
        currentPage={pagination.page}
        pageSize={pagination.per_page}
        loading={loading}
      />
      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        totalCount={totalCount}
        perPage={pagination.per_page}
        onPageChange={handlePageChange}
      />

      <TabSheet
        title="Create Entity"
        form={form}
        mode="create"
        data={null}
        open={createOpen}
        formFields={formFields}
        onOpen={setCreateOpen}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default EntityTab;
